import React from "react";
import { Product } from "../pages/manage-products/manageProducts";

const STORAGE_KEY = "rudrix_product_amazon_links";

/**
 * Retrieve all stored Amazon links from localStorage.
 */
export const getStoredAmazonLinks = (): Record<string, string> => {
  if (typeof window === "undefined" || !window.localStorage) return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    console.error("Error reading amazon links from localStorage", e);
    return {};
  }
};

/**
 * Retrieve Amazon link for a specific product.
 */
export const getStoredAmazonLink = (productId: string): string => {
  if (!productId) return "";
  const links = getStoredAmazonLinks();
  return links[productId] || "";
};

/**
 * Save an Amazon link for a product.
 */
export const setStoredAmazonLink = (productId: string, link: string): void => {
  if (!productId || typeof window === "undefined" || !window.localStorage) return;
  try {
    const links = getStoredAmazonLinks();
    if (link && link.trim()) {
      links[productId] = normalizeAmazonUrl(link.trim());
    } else {
      delete links[productId];
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(links));
  } catch (e) {
    console.error("Error saving amazon link to localStorage", e);
  }
};

/**
 * Remove an Amazon link for a product.
 */
export const removeStoredAmazonLink = (productId: string): void => {
  if (!productId || typeof window === "undefined" || !window.localStorage) return;
  try {
    const links = getStoredAmazonLinks();
    delete links[productId];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(links));
  } catch (e) {
    console.error("Error removing amazon link from localStorage", e);
  }
};

/**
 * Normalize an Amazon URL (ensures http/https protocol).
 */
export const normalizeAmazonUrl = (url: string): string => {
  const trimmed = url.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
};

/**
 * Basic validation for Amazon URLs.
 */
export const isLikelyAmazonUrl = (url: string): boolean => {
  if (!url) return false;
  const lower = url.toLowerCase();
  return (
    lower.includes("amazon.") ||
    lower.includes("amzn.to") ||
    lower.includes("amzn.eu") ||
    lower.includes("amzn.in") ||
    lower.includes("amzn.com")
  );
};

/**
 * Enrich a product with its Amazon link (API field takes precedence, then localStorage).
 */
export const enrichProductWithAmazonLink = (product: any): Product => {
  const storedLink = getStoredAmazonLink(product._id || product.id);
  const directLink = product.amazonLink || product.amazon_link || product.orderLink || product.order_url || "";
  const finalLink = directLink || storedLink || "";

  return {
    ...product,
    amazonLink: finalLink,
  };
};

/**
 * Custom Amazon SVG Icon for UI buttons
 */
export const AmazonIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 18,
  color = "currentColor",
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: "inline-block", verticalAlign: "middle" }}
  >
    {/* Amazon smile / shopping curve */}
    <path
      d="M3 13.5C7.5 17 14 17.5 19.5 14"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M17.5 12L20.5 14L18.5 16.5"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    {/* Package / bag outline */}
    <path
      d="M5 9.5V6.5C5 5.39543 5.89543 4.5 7 4.5H17C18.1046 4.5 19 5.39543 19 6.5V9.5"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);
